/**
 * Money is ALWAYS stored and computed as an integer number of minor units (cents).
 * Never use floats for money. Display formatting happens only at the UI edge.
 */
export type Cents = number;

export const ZERO: Cents = 0;

/** Convert a decimal amount (e.g. 19.99) to cents (1999). Rounds to nearest cent. */
export function toCents(amount: number): Cents {
  return Math.round(amount * 100);
}

/** Convert cents to a decimal number (1999 -> 19.99). For display/serialization only. */
export function fromCents(cents: Cents): number {
  return cents / 100;
}

/** Sum a list of cent amounts safely. */
export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce<Cents>((acc, v) => acc + Math.round(v), 0);
}

/**
 * Apply a percentage (e.g. tax 8.25, discount 10) to a cents base, returning integer cents.
 * `percent` is expressed as a whole/decimal percent, not a fraction (8.25 => 8.25%).
 */
export function percentOf(base: Cents, percent: number): Cents {
  return Math.round((base * percent) / 100);
}

/** Format cents as a currency string. Currency defaults to USD; locale to en-US. */
export function formatMoney(cents: Cents, currency = 'USD', locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(fromCents(cents));
}

/** Clamp to non-negative integer cents (guards against invalid negative charges). */
export function nonNegativeCents(cents: Cents): Cents {
  return Math.max(0, Math.round(cents));
}
